import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { textCmsService } from '../services/textCmsService';
import type { TextsData, TextCmsContextType } from '../types/textCms';

const TextCmsContext = createContext<TextCmsContextType | undefined>(undefined);

interface TextCmsProviderProps {
  children: ReactNode;
  locale?: string;
  fallbackTexts?: TextsData;
}

export const TextCmsProvider: React.FC<TextCmsProviderProps> = ({
  children,
  locale = 'en',
  fallbackTexts = {}
}) => {
  const [texts, setTexts] = useState<TextsData>(fallbackTexts);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState<number>(0);
  const [lastModified, setLastModified] = useState<string | null>(null);

  const loadTexts = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await textCmsService.getTexts(locale);
      setTexts(data.texts);
      setVersion(data.version);
      setLastModified(data.lastModified);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load texts from CMS';
      setError(errorMessage);
      console.error('Error loading texts from CMS:', err);
      
      if (Object.keys(texts).length === 0) {
        setTexts(fallbackTexts);
      }
    } finally {
      setLoading(false);
    }
  };

  const getText = (key: string, defaultValue?: string): string => {
    const keys = key.split('.');
    let value: any = texts;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return defaultValue || key;
      }
    }

    return typeof value === 'string' ? value : defaultValue || key;
  };

  const refreshTexts = async () => {
    await loadTexts();
  };

  useEffect(() => {
    loadTexts();
  }, []);

  const value: TextCmsContextType = {
    texts,
    loading,
    error,
    locale,
    version,
    lastModified,
    getText,
    setLocale: async () => {}, 
    refreshTexts
  };

  return <TextCmsContext.Provider value={value}>{children}</TextCmsContext.Provider>;
};

export const useTextCms = (): TextCmsContextType => {
  const context = useContext(TextCmsContext);
  if (!context) {
    throw new Error('useTextCms must be used within a TextCmsProvider');
  }
  return context;
};

export default TextCmsContext;
